import { computed, type ComputedRef, type Ref } from 'vue';

import type {
  ReturnActionModel,
  ReturnCardModel,
  ReturnHelpLinkModel,
  ReturnItemModel,
} from '../../../api/types/ReturnCardModel';
import { RETURN_HELP_LINKS } from '../config/returnHelpLinks';

/**
* Minimal shape of the order data this composable reads. Kept local and
* narrow on purpose: we only declare the fields we actually touch, so a change
* elsewhere in the order model does not ripple into the return cards.
*/
interface StatusLike {
  statusDate?: string | null;
  summaryStatusCode?: string | null;
  detailStatusCode?: string | null;
}

interface FulfillmentItemLike {
  name?: string;
  quantity?: string | number;
  imageUrl?: string | null;
  status?: StatusLike | null;
}

interface FulfillmentGroupLike {
  deliveryType?: string;
  trackingNumber?: string | null;
  status?: StatusLike | null;
  fulfillmentItems?: FulfillmentItemLike[];
}

interface OrderLike {
  orderId?: string;
  orderNumber?: string;
  billingZipCode?: string | null;
  billingAddress?: { address?: { zip?: string | null } | null } | null;
  fulfillmentGroups?: FulfillmentGroupLike[];
}

/**
* The return signal.
*
* `purchase-details/history` reports it as a fulfillment group with
* `deliveryType: RETURNED`, but the per-order detail endpoint — which is what
* the Help Center actually consumes — keeps the group as DTC and puts the
* signal on each item's status instead. So we read the item codes, which are
* present in both shapes.
*
* 0015 / 1022 is Return Created. Confirmed against Sterling, where the same
* order line reads 3700.01.
*/
const RETURN_SUMMARY_STATUS = '0015';
const RETURN_DETAIL_STATUS = '1022';
const RETURNED_DELIVERY_TYPE = 'RETURNED';

const PURCHASE_DETAILS_PATH = '/purchase-history/purchase-details';
const REFUND_STATUS_URL = '/help/refund-status';
const NARVAR_RETURNS_PATH = '/rei-co-op/returns';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
* Visibility window, from Conman via `return.cards.active.return.days`.
*
* The order data already drops a return once it completes or is cancelled, so
* this is a ceiling on top of that, not the main rule. It exists because a
* customer can create a return label and never ship the package: the return
* stays open indefinitely and the card would sit there forever.
*
* Counted from the return's own status date, which is separate from
* `return.cards.previous.order.days` — that one bounds how far back we search
* for orders, and has to be the wider of the two since a return can be created
* long after the purchase.
*
* One caveat worth knowing: `statusDate` is the date of the item's *current*
* status, not of the return's creation. They are the same while the return has
* not moved, which is exactly the case this window is meant to catch. If a
* return does progress, the date moves with it and the window restarts from
* the new event. Whether that is the desired behaviour has not come up yet.
*/
const isWithinWindow = (
  returnCreatedDate: string | null,
  windowDays: number | undefined,
): boolean => {
  if (!windowDays || windowDays <= 0) return true;
  if (!returnCreatedDate) return true;

  const created = new Date(returnCreatedDate).getTime();
  if (Number.isNaN(created)) return true;

  return (Date.now() - created) / MS_PER_DAY < windowDays;
};

/** Null for a missing or unparseable date, so those sort last rather than first. */
const toTime = (value: string | null): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

/** Most recent return first. Cards without a usable date go to the end. */
const byMostRecent = (a: ReturnCardModel, b: ReturnCardModel): number => {
  const aTime = toTime(a.returnCreatedDate);
  const bTime = toTime(b.returnCreatedDate);

  if (aTime === null && bTime === null) return 0;
  if (aTime === null) return 1;
  if (bTime === null) return -1;

  return bTime - aTime;
};

const isReturnStatus = (status?: StatusLike | null): boolean => status?.summaryStatusCode === RETURN_SUMMARY_STATUS
&& status?.detailStatusCode === RETURN_DETAIL_STATUS;

const isReturningItem = (item: FulfillmentItemLike): boolean => isReturnStatus(item.status);

const buildPurchaseDetailsUrl = (orderId: string): string => `${PURCHASE_DETAILS_PATH}?orderId=${encodeURIComponent(orderId)}&historyId=${encodeURIComponent(orderId)}`;

/**
* Narvar's own returns portal, which opens straight into the customer's return
* when given an order number and a billing ZIP — both of which we already have.
*
* Parameter names taken from what Purchase History navigates to today: `order`
* and `bzip`. Other spellings land on the portal's lookup form instead, which
* makes the customer type in details we already know.
*
* Deliberately not linking straight to the label PDF: that URL carries no
* signature and no expiry, and renders the customer's name and address, so it
* should not sit in the DOM. Sending the customer to the portal gets them to
* the same document through Narvar's own flow.
*/
const buildNarvarPortalUrl = (
  orderId: string,
  billingZipCode: string | null | undefined,
  narvarBaseUrl: string | undefined,
): string | null => {
  if (!billingZipCode || !narvarBaseUrl) return null;

  const params = new URLSearchParams({
    order: orderId,
    bzip: billingZipCode,
  });

  return `${narvarBaseUrl}${NARVAR_RETURNS_PATH}?${params.toString()}`;
};

const toQuantity = (value: string | number | undefined): number => {
  const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
  if (typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0) return parsed;
  return 1;
};

const mapItem = (item: FulfillmentItemLike): ReturnItemModel => ({
  name: item.name ?? '',
  imageUrl: item.imageUrl ?? null,
  quantity: toQuantity(item.quantity),
});

const countItems = (items: ReturnItemModel[]): number => items.reduce((total, item) => total + item.quantity, 0);

/** The detail endpoint nests the ZIP; history exposes it at the top level. */
const resolveBillingZip = (order: OrderLike): string | null => order.billingZipCode ?? order.billingAddress?.address?.zip ?? null;

const buildActions = (
  orderId: string,
  billingZipCode: string | null,
  narvarBaseUrl: string | undefined,
): ReturnActionModel[] => {
  const actions: ReturnActionModel[] = [
    {
      id: 'refund_timing_estimate',
      label: 'Refund timing estimate',
      url: REFUND_STATUS_URL,
      icon: 'calendar',
    },
    {
      id: 'view_refund_details',
      label: 'View order details',
      url: buildPurchaseDetailsUrl(orderId),
      icon: 'card',
    },
  ];

  const portalUrl = buildNarvarPortalUrl(orderId, billingZipCode, narvarBaseUrl);
  if (portalUrl) {
    actions.push({
      id: 'view_return_label',
      label: 'View return status',
      url: portalUrl,
      icon: 'print',
    });
  }

  return actions;
};

/**
* Turns order data into one card per active return.
*
* A group counts as returning when it is marked RETURNED, or when any of its
* items carries the return status. Grouping is per fulfillment group rather
* than per order, so an order with two active returns produces two cards.
*
* Cards outside the visibility window are dropped, and what is left is ordered
* most recent first.
*/
interface UseActiveReturnsOptions {
  /** Environment-specific Narvar host, from Conman via `narvar.baseUrl`. */
  narvarBaseUrl?: string;
  /** Days a card keeps showing after the return's status date. */
  activeReturnDays?: number;
  helpLinks?: ReturnHelpLinkModel[];
}

const useActiveReturns = (
  orders: Ref<OrderLike[]> | ComputedRef<OrderLike[]>,
  options: UseActiveReturnsOptions = {},
): ComputedRef<ReturnCardModel[]> => computed(() => (orders.value ?? []).flatMap((order) => {
  const orderId = order.orderId ?? order.orderNumber;
  if (!orderId) return [];

  const billingZipCode = resolveBillingZip(order);

  return (order.fulfillmentGroups ?? []).flatMap((group, index) => {
    const groupIsReturned = group.deliveryType === RETURNED_DELIVERY_TYPE;
    const allItems = group.fulfillmentItems ?? [];
    const returningItems = groupIsReturned
      ? allItems
      : allItems.filter(isReturningItem);

    if (!returningItems.length) return [];

    const items = returningItems.map(mapItem);

    // The item status carries the date the return was created; the group
    // status does not move when a return is created on the detail endpoint.
    const returnCreatedDate = returningItems
      .map((item) => item.status?.statusDate)
      .find(Boolean) ?? group.status?.statusDate ?? null;

    return [{
      // Tracking number is the most stable discriminator when an order has
      // more than one return; index is the fallback when it is absent.
      key: `${orderId}-${group.trackingNumber ?? index}`,
      orderNumber: orderId,
      returnCreatedDate,
      items,
      itemsReturningCount: countItems(items),
      actions: buildActions(orderId, billingZipCode, options.narvarBaseUrl),
      relatedHelp: options.helpLinks ?? RETURN_HELP_LINKS,
    }];
  });
})
  .filter((card) => isWithinWindow(card.returnCreatedDate, options.activeReturnDays))
  .sort(byMostRecent));

export default useActiveReturns;
